import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { AdminFeaturesService } from '../service/admin-features.service';

/**
 * Affiliation - ISSO's side.
 *
 * The standard is 50,000 for twelve months, and everything about one school is
 * on one screen: what they are being asked to pay, what they have paid, and the
 * two ways money arrives outside the gateway (a cheque ISSO records, and the
 * old Yes/No flag that needs an end date).
 */
@Component({
  selector: 'app-affiliation-admin',
  templateUrl: './affiliation-admin.component.html',
  styleUrls: ['./affiliation-admin.component.css'],
  providers: [MessageService],
})
export class AffiliationAdminComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  /** Standard amounts, applied to every school without an override. */
  amounts: any = { annual: 50000, sport: 5000, banner: true };
  savingAmounts = false;
  /** Both editors stay shut: these are settings, not daily work. */
  editAmounts = false;
  editOffer = false;

  schools: any[] = [];
  games: any[] = [];
  search = '';
  /** Which schools the list is showing: all, paid, carried over, or unpaid. */
  filter: 'all' | 'paid' | 'expired' | 'legacy' | 'unpaid' = 'all';
  counts: any = { all: 0, paid: 0, expired: 0, legacy: 0, unpaid: 0 };
  listLoading = false;
  private searchTimer: any = null;
  schoolId: number | null = null;
  detail: any = null;
  loading = false;

  offer: any   = { annualAmount: null, annualMonths: null, sportId: null, sportAmount: null };
  /**
   * One of three, never a combination: the standard annual fee, an amount
   * agreed with this school, or the single-sport rate in place of the year.
   */
  mode: 'standard' | 'custom' | 'sport' = 'standard';
  record: any  = { kind: 'annual', sportId: null, amount: null, months: 12, validUntil: '', paidAt: '', note: '', carriedOver: false };

  recordOpen = false;

  constructor(private svc: AdminFeaturesService, private toast: MessageService) {}

  ngOnInit() {
    this.svc.affiliationAmounts().pipe(takeUntil(this.destroy$))
      .subscribe((a: any) => this.amounts = a || this.amounts);
    this.loadSchools();
    this.svc.gameList().pipe(takeUntil(this.destroy$))
      .subscribe((g: any[]) => this.games = (g || []).map(x => ({ id: x.id || x.gameId, name: x.name || x.gameName })));
  }

  /** The filter and the search are both resolved server-side. */
  loadSchools() {
    this.listLoading = true;
    this.svc.affiliationSchools(this.filter, this.search.trim())
      .pipe(takeUntil(this.destroy$))
      .subscribe((d: any) => {
        this.schools = (d && d.rows) || [];
        this.counts = (d && d.counts) || this.counts;
        this.listLoading = false;
      }, () => { this.listLoading = false; this.err('Could not load the schools'); });
  }

  setFilter(f: 'all' | 'paid' | 'expired' | 'legacy' | 'unpaid') { this.filter = f; this.loadSchools(); }

  /** Typing shouldn't fire a request per keystroke. */
  onSearch() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadSchools(), 300);
  }

  /** The list is long; the screen shows the first 100 of whatever matched. */
  get visibleSchools() { return this.schools.slice(0, 100); }

  stateLabel(s: string) {
    return s === 'paid' ? 'Paid'
      : s === 'expired' ? 'Expired'
      : s === 'legacy' ? 'Affiliated (old flag)'
      : 'Not affiliated';
  }

  /** The one line that says where a school stands, dates included. */
  get standing(): string {
    const d = this.detail;
    if (!d) return '';
    const until = d.annual && d.annual.validUntil;
    if (this.isPaid && until) return `Paid - valid till ${this.dateText(until)}`;
    if (this.isPaid) return 'Paid';
    if (this.isLegacyOnly) return 'Affiliated on the old flag - no end date set';
    if (d.expiredOn) return `Expired on ${this.dateText(d.expiredOn)}`;
    return 'Never affiliated';
  }

  dateText(d: any) {
    const dt = new Date(d);
    return dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  get standingState(): string {
    if (this.isPaid) return 'paid';
    if (this.isLegacyOnly) return 'legacy';
    return (this.detail && this.detail.expiredOn) ? 'expired' : 'unpaid';
  }

  /** The banner switch saves immediately: it is a tap, not a form. */
  toggleBanner(on: boolean) {
    this.amounts.banner = on;
    this.svc.setAffiliationAmounts({ banner: on }).pipe(takeUntil(this.destroy$)).subscribe((a: any) => {
      this.amounts = a;
      this.ok(on ? 'Schools will see the payment banner' : 'Payment banner switched off');
    }, () => { this.amounts.banner = !on; this.err('Could not change that'); });
  }

  openAmounts() { this.amountsBackup = { ...this.amounts }; this.editAmounts = true; }
  cancelAmounts() { if (this.amountsBackup) this.amounts = this.amountsBackup; this.editAmounts = false; }
  private amountsBackup: any = null;

  openOffer() { this.offerBackup = { mode: this.mode, offer: { ...this.offer } }; this.editOffer = true; }
  cancelOffer() {
    if (this.offerBackup) { this.mode = this.offerBackup.mode; this.offer = { ...this.offerBackup.offer }; }
    this.editOffer = false;
  }
  private offerBackup: any = null;

  saveAmounts() {
    const b = {
      annual: Number(this.amounts.annual),
      sport:  Number(this.amounts.sport),
      banner: !!this.amounts.banner,
    };
    if (!(b.annual > 0) || !(b.sport > 0)) { this.err('Both amounts must be more than zero'); return; }
    this.savingAmounts = true;
    this.svc.setAffiliationAmounts(b).pipe(takeUntil(this.destroy$)).subscribe((a: any) => {
      this.amounts = a; this.savingAmounts = false; this.editAmounts = false;
      this.ok('Standard amounts saved');
    }, () => { this.savingAmounts = false; this.err('Could not save the amounts'); });
  }

  pick(id: number) { this.schoolId = id; this.load(); }

  load() {
    if (!this.schoolId) return;
    this.loading = true;
    this.svc.affiliationForSchool(this.schoolId).pipe(takeUntil(this.destroy$)).subscribe((d: any) => {
      this.detail = d;
      this.loading = false;
      // The form starts on what this school is actually being offered, so an
      // edit never silently resets a waiver back to the standard.
      // `offer` is what ISSO has overridden; anything unset is the standard, and
      // the screen says so rather than pre-filling the standard as if it were typed.
      const raw = (d && d.offer) || {};
      if (d && d.standard) this.amounts = d.standard;
      this.editOffer = false;
      this.mode = raw.sportOnly ? 'sport'
        : (raw.annualAmount != null || raw.annualMonths != null) ? 'custom'
        : 'standard';
      this.offer = {
        annualAmount: raw.annualAmount != null ? raw.annualAmount : this.amounts.annual,
        annualMonths: raw.annualMonths != null ? raw.annualMonths : 12,
        sportId:      raw.sportId != null ? raw.sportId : null,
        sportAmount:  raw.sportAmount != null ? raw.sportAmount : this.amounts.sport,
      };
    }, () => { this.loading = false; this.err('Could not load that school'); });
  }

  /** Each mode starts from a sensible figure rather than an empty box. */
  onMode() {
    if (this.mode === 'standard') {
      this.offer.annualAmount = this.amounts.annual;
      this.offer.annualMonths = 12;
    }
    if (this.mode === 'sport' && this.offer.sportAmount == null) {
      this.offer.sportAmount = this.amounts.sport;
    }
  }

  /** The heading of the closed card: which of the three this school is on. */
  get modeLabel(): string {
    if (this.mode === 'sport') return 'One sport only, instead of the year';
    if (this.mode === 'custom') return 'A special annual amount for this school';
    return 'Standard annual fee';
  }

  /** What the school will actually see, in one sentence. */
  get summary(): string {
    const months = this.offer.annualMonths || 12;
    if (this.mode === 'sport') {
      const sport = this.offer.sportId ? this.gameName(this.offer.sportId) : 'the chosen sport';
      return `This school is asked for ${this.money(this.offer.sportAmount)} for ${sport} only. `
           + 'Every other sport stays at the unaffiliated entry rate.';
    }
    const amount = this.money(this.mode === 'custom' ? this.offer.annualAmount : this.amounts.annual);
    return `This school is asked for ${amount} for ${months} months, covering every sport.`;
  }

  saveOffer() {
    if (!this.schoolId) return;
    if (this.mode === 'custom' && !(Number(this.offer.annualAmount) > 0)) { this.err('Enter the amount this school pays'); return; }
    if (this.mode === 'sport' && !this.offer.sportId) { this.err('Pick the sport being offered'); return; }
    const b = {
      annualAmount: this.mode === 'custom' ? Number(this.offer.annualAmount) : null,
      annualMonths: this.mode === 'custom' ? Number(this.offer.annualMonths) || 12 : null,
      sportId:      this.mode === 'sport' ? Number(this.offer.sportId) : null,
      sportAmount:  this.mode === 'sport' && this.offer.sportAmount != null && this.offer.sportAmount !== ''
        ? Number(this.offer.sportAmount) : null,
      sportOnly:    this.mode === 'sport',
    };
    this.svc.setAffiliationOffer(this.schoolId, b).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.ok('Saved. The school sees this amount in their portal.');
      this.editOffer = false; this.load(); this.loadSchools();
    }, () => this.err('Could not save'));
  }

  openRecord() {
    this.record = {
      kind: 'annual', sportId: null,
      amount: this.offeredAnnualAmount,
      months: this.offeredAnnualMonths,
      validUntil: '',
      paidAt: new Date().toISOString().slice(0, 10),
      note: '',
      // Pre-ticked for a school that is only affiliated because of the old flag:
      // that is exactly the case where there is no payment to record.
      carriedOver: this.isLegacyOnly,
    };
    this.recordOpen = true;
  }

  onRecordKind() {
    this.record.amount = this.record.kind === 'sport' ? this.offeredSportAmount : this.offeredAnnualAmount;
  }

  saveRecord() {
    if (!this.schoolId) return;
    if (this.record.kind === 'sport' && !this.record.sportId) { this.err('Pick the sport'); return; }
    if (!this.record.carriedOver && !(Number(this.record.amount) >= 0)) { this.err('Enter the amount received'); return; }
    if (this.record.carriedOver && !this.record.validUntil) { this.err('Enter the date their affiliation runs to'); return; }
    const b = {
      kind:        this.record.kind,
      sportId:     this.record.kind === 'sport' ? Number(this.record.sportId) : null,
      amount:      this.record.carriedOver ? undefined : Number(this.record.amount),
      months:      Number(this.record.months) || 12,
      validUntil:  this.record.validUntil || undefined,
      paidAt:      this.record.carriedOver ? undefined : (this.record.paidAt || undefined),
      note:        this.record.note || undefined,
      carriedOver: this.record.carriedOver,
    };
    this.svc.recordAffiliation(this.schoolId, b).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.ok(this.record.carriedOver ? 'Affiliation recorded' : 'Payment recorded');
      this.recordOpen = false; this.load(); this.loadSchools();
    }, (e: any) => this.err((e && e.error && e.error.message) || 'Could not record that'));
  }

  cancelRow(r: any) {
    if (!confirm(`Cancel this ${r.kind} affiliation? The school loses the affiliated rate immediately.`)) return;
    this.svc.cancelAffiliation(r.id).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.ok('Cancelled'); this.load(); this.loadSchools();
    }, () => this.err('Could not cancel'));
  }

  schoolName(id: number) {
    const s = this.schools.find(x => x.id === id);
    return s ? s.name : `School ${id}`;
  }

  /** The invoice for a recorded payment, fetched with the admin's token. */
  downloadReceipt(h: any) {
    if (!h.paymentId) return;
    this.svc.paymentReceipt(h.paymentId).pipe(takeUntil(this.destroy$)).subscribe((blob: any) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(h.receiptNo || 'receipt-' + h.paymentId).replace(/[^\w.-]+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, () => this.err('Could not download that receipt'));
  }
  gameName(id: number) { const g = this.games.find(x => x.id === id); return g ? g.name : ''; }

  money(n: any) { return n == null ? '-' : `Rs. ${Number(n).toLocaleString('en-IN')}`; }

  /** Live, paid annual cover - the only state that counts as "Paid". */
  get isPaid() { return !!(this.detail && this.detail.annual && this.detail.annual.paid); }
  /** Affiliated only because the old flag is still set, with no payment on record. */
  get isLegacyOnly() { return !!(this.detail && this.detail.annual && this.detail.annual.legacyOnly); }

  /** What this school is being asked for, falling back to the standard. */
  get offeredAnnualAmount() {
    const a = this.detail && this.detail.annual && this.detail.annual.amount;
    return a != null ? a : this.amounts.annual;
  }
  get offeredAnnualMonths() {
    const m = this.detail && this.detail.annual && this.detail.annual.months;
    return m != null ? m : 12;
  }
  get offeredSportAmount() {
    const a = this.detail && this.detail.sportOffer && this.detail.sportOffer.amount;
    return a != null ? a : this.amounts.sport;
  }

  private ok(m: string)  { this.toast.add({ severity: 'success', summary: m }); }
  private err(m: string) { this.toast.add({ severity: 'error', summary: m }); }

  ngOnDestroy() { this.destroy$.next(); this.destroy$.complete(); }
}
